/**
 * Doc-model store (React context + reducer) — the frontend's single source of
 * truth for the ProjectDoc and conversation (CLAUDE.md §3/§5).
 *
 * Responsibilities:
 *  - Hold the canonical ProjectDoc + chat messages.
 *  - Apply streamed `field` deltas live (so the right pane updates as Claude streams).
 *  - Append streamed assistant `token`s smoothly.
 *  - Debounced autosave to the backend on any edit.
 *  - Track which fields just changed so the preview can highlight them briefly.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  type ReactNode,
} from "react";
import {
  emptyProjectDoc,
  MAX_STRETCH_GOALS,
  type ChatMessage,
  type ChatRole,
  type ProjectDoc,
  type ProjectDocField,
} from "../lib/types";
import {
  chat as streamChat,
  getDraft,
  resetDraft as apiResetDraft,
  saveDraft,
} from "../lib/apiClient";

// ---------------------------------------------------------------------------
// State + actions
// ---------------------------------------------------------------------------

type SaveStatus = "idle" | "saving" | "saved" | "error";

interface DocState {
  doc: ProjectDoc;
  messages: ChatMessage[];
  /** Set of field keys recently updated (drives the highlight animation). */
  recentlyChanged: Set<ProjectDocField>;
  loading: boolean;
  streaming: boolean;
  saveStatus: SaveStatus;
  error: string | null;
}

type Action =
  | { type: "loaded"; doc: ProjectDoc; messages: ChatMessage[] }
  | { type: "reset" }
  | { type: "setField"; name: ProjectDocField; value: string | string[] }
  | { type: "patchDoc"; patch: Partial<ProjectDoc> }
  | { type: "addMessage"; message: ChatMessage }
  | { type: "appendToken"; id: string; text: string }
  | { type: "endStreaming"; id: string }
  | { type: "setStreaming"; streaming: boolean }
  | { type: "clearRecent"; name: ProjectDocField }
  | { type: "setSaveStatus"; status: SaveStatus }
  | { type: "setError"; error: string | null };

/** Opening coach message so users aren't faced with a silent, empty chat. */
export const WELCOME_TEXT = `Welcome — I'm your LADP Scoping Assistant. I'll help you shape your idea into a focused project you can realistically **build in one month**.

How this works:
1. We'll start with a few basics: your **company name**, **your name**, and a **contact email**, then the one main goal for the project.
2. I'll then walk you through the planning questions one at a time, keeping the scope tight enough to finish in a month.
3. The document on the right fills in automatically as we talk. You can also edit any field directly.
4. When you're happy with it, select **Submit** to send it to the LADP team. You'll receive your own copy and can download it as Word or PDF anytime.

Not sure about something (like the tech stack)? Just say so, or ask me for suggestions — I'll recommend something sensible.

To begin: **what is your company name?**`;

function initialState(): DocState {
  return {
    doc: emptyProjectDoc(),
    messages: [],
    recentlyChanged: new Set(),
    loading: true,
    streaming: false,
    saveStatus: "idle",
    error: null,
  };
}

function markChanged(
  prev: Set<ProjectDocField>,
  name: ProjectDocField,
): Set<ProjectDocField> {
  const next = new Set(prev);
  next.add(name);
  return next;
}

function reducer(state: DocState, action: Action): DocState {
  switch (action.type) {
    case "loaded":
      return {
        ...state,
        doc: action.doc,
        // Don't clobber an in-progress conversation if the draft loads late.
        messages: state.messages.length ? state.messages : action.messages,
        loading: false,
      };
    case "reset":
      // Fresh blank document + empty conversation.
      return { ...initialState(), loading: false };
    case "setField": {
      // `stretchGoals` is an array; everything else is a string.
      const value =
        action.name === "stretchGoals"
          ? (Array.isArray(action.value)
              ? action.value
              : [String(action.value)]
            ).slice(0, MAX_STRETCH_GOALS)
          : Array.isArray(action.value)
            ? action.value.join("\n")
            : action.value;
      return {
        ...state,
        doc: { ...state.doc, [action.name]: value } as ProjectDoc,
        recentlyChanged: markChanged(state.recentlyChanged, action.name),
      };
    }
    case "patchDoc":
      return { ...state, doc: { ...state.doc, ...action.patch } };
    case "addMessage":
      return { ...state, messages: [...state.messages, action.message] };
    case "appendToken":
      return {
        ...state,
        messages: state.messages.map((m) =>
          m.id === action.id
            ? { ...m, content: m.content + action.text }
            : m,
        ),
      };
    case "endStreaming":
      return {
        ...state,
        messages: state.messages.map((m) =>
          m.id === action.id ? { ...m, streaming: false } : m,
        ),
      };
    case "setStreaming":
      return { ...state, streaming: action.streaming };
    case "clearRecent": {
      if (!state.recentlyChanged.has(action.name)) return state;
      const next = new Set(state.recentlyChanged);
      next.delete(action.name);
      return { ...state, recentlyChanged: next };
    }
    case "setSaveStatus":
      return { ...state, saveStatus: action.status };
    case "setError":
      return { ...state, error: action.error };
    default:
      return state;
  }
}

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

interface DocStore extends DocState {
  /** Update a single field (user edit) — marks it changed and triggers autosave. */
  setField: (name: ProjectDocField, value: string | string[]) => void;
  /** Send a chat message and stream the assistant reply + field deltas. */
  sendMessage: (text: string) => Promise<void>;
  /** Cancel an in-flight stream. */
  cancelStream: () => void;
  /** Acknowledge a highlight so it can fade out. */
  clearRecent: (name: ProjectDocField) => void;
  /** Clear the draft (blank doc + conversation) to start a new document. */
  resetDraft: () => Promise<void>;
}

const Ctx = createContext<DocStore | undefined>(undefined);

const AUTOSAVE_DELAY = 800;

export function DocStoreProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(reducer, undefined, initialState);

  const docRef = useRef(state.doc);
  docRef.current = state.doc;

  const messagesRef = useRef(state.messages);
  messagesRef.current = state.messages;

  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  // --- Initial load of the persisted draft + conversation ---
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const data = await getDraft();
        if (!active) return;
        dispatch({
          type: "loaded",
          doc: { ...emptyProjectDoc(), ...data.doc },
          messages: data.messages ?? [],
        });
      } catch {
        // No draft yet (or backend unavailable) — start fresh.
        if (active)
          dispatch({
            type: "loaded",
            doc: emptyProjectDoc(),
            messages: [],
          });
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  // --- Debounced autosave ---
  const scheduleSave = useCallback(() => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      dispatch({ type: "setSaveStatus", status: "saving" });
      try {
        await saveDraft(docRef.current);
        dispatch({ type: "setSaveStatus", status: "saved" });
      } catch {
        dispatch({ type: "setSaveStatus", status: "error" });
      }
    }, AUTOSAVE_DELAY);
  }, []);

  useEffect(() => {
    return () => {
      if (saveTimer.current) clearTimeout(saveTimer.current);
    };
  }, []);

  const setField = useCallback(
    (name: ProjectDocField, value: string | string[]) => {
      dispatch({ type: "setField", name, value });
      scheduleSave();
    },
    [scheduleSave],
  );

  const clearRecent = useCallback((name: ProjectDocField) => {
    dispatch({ type: "clearRecent", name });
  }, []);

  const cancelStream = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    dispatch({ type: "setStreaming", streaming: false });
  }, []);

  const resetDraft = useCallback(async () => {
    abortRef.current?.abort();
    abortRef.current = null;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    dispatch({ type: "reset" });
    try {
      await apiResetDraft();
    } catch {
      /* best effort — local state is already cleared */
    }
  }, []);

  const sendMessage = useCallback(
    async (text: string) => {
      const trimmed = text.trim();
      // Guard via the ref (always current) so a rapid double-send / double-Enter
      // can't start two overlapping streams writing into the same bubble.
      if (!trimmed || abortRef.current) return;
      // Claim the slot synchronously so a near-simultaneous second call bails out.
      const controller = new AbortController();
      abortRef.current = controller;

      const now = Date.now();
      const userMsg: ChatMessage = {
        id: `u-${now}`,
        role: "user",
        content: trimmed,
        createdAt: now,
      };
      const assistantId = `a-${now}`;
      const assistantMsg: ChatMessage = {
        id: assistantId,
        role: "assistant",
        content: "",
        streaming: true,
        createdAt: now + 1,
      };

      dispatch({ type: "addMessage", message: userMsg });
      dispatch({ type: "addMessage", message: assistantMsg });
      dispatch({ type: "setStreaming", streaming: true });
      dispatch({ type: "setError", error: null });

      // Full conversation = prior turns (messagesRef is pre-dispatch here) + this user turn.
      const history = messagesRef.current.map((m) => ({
        role: m.role as ChatRole,
        content: m.content,
      }));
      const convo = [
        ...history,
        { role: "user" as ChatRole, content: trimmed },
      ];

      let fieldsTouched = false;
      let gotToken = false;

      await streamChat(
        { messages: convo, doc: docRef.current },
        {
          onToken: (delta) => {
            gotToken = true;
            dispatch({ type: "appendToken", id: assistantId, text: delta });
          },
          onField: (name, value) => {
            fieldsTouched = true;
            dispatch({ type: "setField", name, value });
          },
          onDone: () => {
            // Safety net: the model sometimes returns only field updates with no
            // prose. Never leave a blank coach bubble (it looks like a hang).
            if (!gotToken) {
              dispatch({
                type: "appendToken",
                id: assistantId,
                text: fieldsTouched
                  ? "Done — I've updated the document on the right. Shall we move on to the next question?"
                  : "Sorry, I didn't quite catch that — could you rephrase?",
              });
            }
            dispatch({ type: "endStreaming", id: assistantId });
            dispatch({ type: "setStreaming", streaming: false });
            abortRef.current = null;
            if (fieldsTouched) scheduleSave();
          },
          onError: (message) => {
            dispatch({ type: "endStreaming", id: assistantId });
            dispatch({ type: "setStreaming", streaming: false });
            dispatch({ type: "setError", error: message });
            abortRef.current = null;
          },
        },
        controller.signal,
      );
    },
    [scheduleSave],
  );

  const value = useMemo<DocStore>(
    () => ({
      ...state,
      setField,
      sendMessage,
      cancelStream,
      clearRecent,
      resetDraft,
    }),
    [state, setField, sendMessage, cancelStream, clearRecent, resetDraft],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useDocStore(): DocStore {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useDocStore must be used within <DocStoreProvider>");
  return ctx;
}
